import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { imageUrl } from '../lib/images';
import { generateEventId, trackMetaLead } from '../lib/metaTracking';

export default function DaekcenterPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    vehicleType: 'personbil',
    message: '',
    website_url: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const eventIdRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');

    if (!eventIdRef.current) {
      eventIdRef.current = generateEventId();
    }
    const currentEventId = eventIdRef.current;

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: `Forespørgsel til Dækcenter (Køretøjstype: ${formData.vehicleType})\n\nBesked:\n${formData.message}`,
        website_url: formData.website_url,
        service_subject: 'Dækcenter 1/11-2026'
      };

      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Kunne ikke indsende henvendelsen.');

      await trackMetaLead({
        eventId: currentEventId,
        email: formData.email,
        phone: formData.phone,
        contentName: `Dækcenter: ${formData.vehicleType}`
      });

      eventIdRef.current = null;
      setSubmitted(true);
      setFormData({ name: '', phone: '', email: '', vehicleType: 'personbil', message: '', website_url: '' });
    } catch (err) {
      setErrorMsg(err.message || 'Der opstod en fejl. Ring venligst til 70 700 800.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Dækcenter i Hedensted | Åbner 1/11-2026 | MC Transport"
        description="Nyt dækcenter hos MC Transport fra 1. november 2026. Komplet dækservice, montering og dækhotel til personbiler, varevogne og lastbiler i Hedensted."
      />

      {/* Hero */}
      <section className="relative bg-zinc-950 text-white py-16 lg:py-24 border-b border-zinc-800 overflow-hidden">
        <img
          src={imageUrl('https://assets.auxodev.dk/assets/7ab2fd9d-1ad5-42a7-a15d-22229bea7818/service-vogn-1-198.jpg', 'width=1920,quality=80,format=auto')}
          alt="MC Transport Dækcenter og mobil servicevogn i Hedensted"
          className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.35]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center sm:text-left">
          <div className="max-w-3xl">
            {/* Planned service badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold uppercase tracking-wider mb-4 border border-amber-500/40">
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Planlagt ydelse · Åbner 1. november 2026</span>
            </div>

            <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white leading-tight">
              MC Transport Dækcenter
            </h1>

            <p className="mt-4 text-base sm:text-lg text-zinc-200 leading-relaxed font-normal">
              Hos <Link to="/" className="font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors">MC Transport</Link> udvider vi med et helt nyt, professionelt dækcenter på <Link to="/kontakt" className="text-zinc-200 hover:text-white underline decoration-zinc-400 underline-offset-2 transition-colors">vores plads i Hedensted</Link>. Fra den 1. november 2026 står vi klar med topmoderne udstyr til dækskifte, afbalancering og dækhotel for både personbiler, varevogne og den tunge vognpark.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#tilmeld"
                className="px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded shadow transition-colors"
              >
                Få besked ved åbningen
              </a>
              <a
                href="tel:+4570700800"
                className="px-6 py-3.5 bg-zinc-900/90 hover:bg-zinc-800 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded border border-white/20 transition-colors"
              >
                Vagtcentral: +45 70 700 800
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Notice Banner */}
      <div className="bg-amber-500 text-zinc-950 py-3 px-4 text-center font-bold text-xs sm:text-sm uppercase tracking-wide">
        <i className="fa-solid fa-circle-info mr-2" aria-hidden="true" />
        Bemærk: Dækcentret åbner officielt 1. november 2026. For akut dækassistance og lapning her og nu benyt vores 24/7 <Link to="/ydelser/servicevogn" className="underline font-black hover:text-red-950">servicevogn</Link> eller kontakt vores <Link to="/autohjaelp" className="underline font-black hover:text-red-950">autohjælp</Link>.
      </div>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
              Kommende Faciliteter
            </span>
            <h2 className="font-condensed text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-zinc-950 mt-1">
              Hvad kan du forvente i dækcentret?
            </h2>
            <p className="text-sm sm:text-base text-zinc-600 mt-2">
              Dækcentret indrettes med nyeste dækmaskiner og sikre opbevaringsfaciliteter i Hedensted.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="w-12 h-12 rounded-lg bg-red-600 text-white flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-car" aria-hidden="true" />
              </div>
              <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-900 mb-2">
                Personbiler & Varevogne
              </h3>
              <ul className="text-xs sm:text-sm text-zinc-600 space-y-2">
                <li>• Sæsonskift sommer/vinter</li>
                <li>• Montering og afbalancering</li>
                <li>• Kvalitetsdæk fra førende mærker</li>
              </ul>
            </div>

            <div className="p-6 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="w-12 h-12 rounded-lg bg-red-600 text-white flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-truck" aria-hidden="true" />
              </div>
              <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-900 mb-2">
                Lastbiler & Tungvogn
              </h3>
              <ul className="text-xs sm:text-sm text-zinc-600 space-y-2 mb-4">
                <li>• Montering af lastbils- og trailerdæk</li>
                <li>• Regummiering og mønsterskæring</li>
                <li>• Nødservice og hjulskift på vejen</li>
                <li>• Erhvervsaftaler til vognmænd</li>
              </ul>
              <div className="pt-3 border-t border-zinc-200 space-y-2">
                <Link to="/daekcenter-lastbil" className="text-xs font-semibold text-red-600 hover:text-red-700 flex items-center gap-1">
                  <span>Læs om Dækcenter til lastbil</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" />
                </Link>
                <Link to="/daekskift-lastbil" className="text-xs font-semibold text-red-600 hover:text-red-700 flex items-center gap-1">
                  <span>Læs om Dækskift til lastbil</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" />
                </Link>
              </div>
            </div>

            <div className="p-6 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="w-12 h-12 rounded-lg bg-red-600 text-white flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-warehouse" aria-hidden="true" />
              </div>
              <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-900 mb-2">
                Dækhotel & Opbevaring
              </h3>
              <ul className="text-xs sm:text-sm text-zinc-600 space-y-2">
                <li>• Tørt, mørkt og tempereret lager</li>
                <li>• Hjulvask og mønstermåling før opbevaring</li>
                <li>• Forsikret opbevaring i Hedensted</li>
                <li>• Nem tidsbestilling ved næste sæson</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section id="tilmeld" className="py-16 bg-zinc-100 border-t border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 sm:p-10 rounded-2xl shadow-xl border border-zinc-200">
            <div className="text-center mb-6">
              <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
                Åbning 1. november 2026
              </span>
              <h3 className="font-condensed text-3xl font-extrabold uppercase text-zinc-950 mt-1">
                Forhåndsreserver eller hør mere
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 mt-2">
                Er du virksomhed med en flåde eller privatbilist, der ønsker besked om åbningstilbud? Skriv til os herunder.
              </p>
            </div>

            {submitted ? (
              <div className="p-6 bg-emerald-50 border border-emerald-300 rounded-lg text-emerald-800 text-center">
                <i className="fa-solid fa-circle-check text-3xl text-emerald-600 mb-2" aria-hidden="true" />
                <h4 className="font-bold text-lg mb-1">Tak for din interesse!</h4>
                <p className="text-xs sm:text-sm">
                  Vi har registreret din henvendelse og vender tilbage med information frem mod åbningen den 1. november 2026.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field */}
                <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website_url">Lad stå tom</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Navn / Virksomhed *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Dit navn eller firmanavn"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full text-xs p-3 bg-zinc-50 border border-zinc-300 rounded focus:border-red-600 focus:bg-white focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="f.eks. 70 700 800"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full text-xs p-3 bg-zinc-50 border border-zinc-300 rounded focus:border-red-600 focus:bg-white focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="din@email.dk"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full text-xs p-3 bg-zinc-50 border border-zinc-300 rounded focus:border-red-600 focus:bg-white focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Køretøjstype
                    </label>
                    <select
                      value={formData.vehicleType}
                      onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                      className="w-full text-xs p-3 bg-zinc-50 border border-zinc-300 rounded focus:border-red-600 focus:bg-white focus:outline-none"
                    >
                      <option value="personbil">Personbil</option>
                      <option value="varevogn">Varevogn / Kassevogn</option>
                      <option value="lastbil">Lastbil / Trækker</option>
                      <option value="trailer">Trailer / Sættevogn</option>
                      <option value="flere">Flådeaftale (flere biler)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Bemærkninger eller særlige dækbehov
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Fortæl gerne om specifikke dækstørrelser, mærker eller ønske om dækhotel..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full text-xs p-3 bg-zinc-50 border border-zinc-300 rounded focus:border-red-600 focus:bg-white focus:outline-none"
                  />
                </div>

                {errorMsg && (
                  <p className="text-xs text-red-600 font-semibold">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded shadow transition-colors"
                >
                  {submitting ? 'Sender...' : 'Send henvendelse'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
